import type YouTube from "@vip30/react-youtube";
import { type FunctionComponent, useCallback, useEffect, useMemo, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import PlayerStates from "youtube-player/dist/constants/PlayerStates";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { notEmpty } from "shared/lib/utils";

import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import { SCMPYouTubeVideoPlayer } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player";
import { getVideoYoutubeId } from "scmp-app/components/video/helpers";
import { useVideoShortCarouselContext } from "scmp-app/components/video/video-short-carousel/contexts";
import { StyledCloseButtonIcon } from "scmp-app/components/video/video-short-carousel/video-short-carousel-player-modal/styles";
import type { videoShortCarouselPlayerContent$key } from "scmp-app/queries/__generated__/videoShortCarouselPlayerContent.graphql";

import {
  ButtonContainer,
  CloseButton,
  Container,
  MuteButton,
  PlayerContainer,
  PlayerOverlay,
  RedirectButton,
  StyledShareButton,
  TitleOverlay,
  TitleText,
} from "./styles";

type Props = {
  className?: string;
  onClose?: () => void;
  reference?: videoShortCarouselPlayerContent$key;
};

export const VideoShortCarouselPlayer: FunctionComponent<Props> = ({
  className,
  onClose,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment videoShortCarouselPlayerContent on Content {
        ... on Video {
          entityId
          headline
          ...helpersGetVideoYoutubeIdVideo
        }
        ...actionWidgetContent
        ...entityLink
        ...shareArticle20Content
      }
    `,
    reference,
  );
  const { entityId, headline } = content ?? {};

  const { modalState, registerPlayer, setPlayerDimension, toggleMuteVideo, unregisterPlayer } =
    useVideoShortCarouselContext();

  const playerReference = useRef<YouTube>(null);
  const buttonContainerReference = useRef<HTMLDivElement>(null);

  const youtubeId = useMemo(() => getVideoYoutubeId(content), [content]);

  const isActiveSlide = modalState?.activeEntityId === entityId;
  const isPlayable = isActiveSlide && (modalState?.isOpen ?? false);
  const isValid = notEmpty(entityId) && notEmpty(youtubeId) && notEmpty(content);

  useEffect(() => {
    if (!entityId) return;

    registerPlayer?.(entityId, playerReference);

    return () => {
      unregisterPlayer?.(entityId);
    };
  }, [entityId, registerPlayer, unregisterPlayer]);

  useEffect(() => {
    const player = playerReference.current?.internalPlayer as undefined | YouTubePlayer;
    if (!notEmpty(player)) return;

    if (isPlayable) {
      void player.playVideo();
    } else {
      void player.pauseVideo();
    }
  }, [isPlayable]);

  useEffect(() => {
    const player = playerReference.current?.internalPlayer as undefined | YouTubePlayer;
    if (!notEmpty(player)) return;

    void (modalState?.mute ? player.mute() : player.unMute());
  }, [modalState?.mute, playerReference]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const player = playerReference.current?.internalPlayer as undefined | YouTubePlayer;
      // Check if click is inside ButtonContainer
      if (buttonContainerReference.current?.contains(event.target as Node) || !notEmpty(player)) {
        return;
      }

      const handleVideoPlayState = async () => {
        const isPlaying = (await player.getPlayerState()) === PlayerStates.PLAYING;
        await (isPlaying ? player.pauseVideo() : player.playVideo());
      };

      void handleVideoPlayState();
    },
    [playerReference],
  );

  if (!isValid) return null;

  return (
    <Container $isActive={isActiveSlide} className={className}>
      <PlayerContainer $isActive={isActiveSlide}>
        <SCMPYouTubeVideoPlayer
          adInfo={{ targeting: { overlay_short: "true" } }}
          controls={false}
          enableAutoPlay={isPlayable}
          entityId={entityId}
          gaData={{
            ga4ArticleEntityId: entityId,
            ga4Position: "scmp_shorts",
          }}
          height="100%"
          index={Number.parseInt(entityId) + 1}
          isMuteVideo={modalState?.mute}
          onDimensionMeasured={dimension => {
            if (entityId) setPlayerDimension?.(entityId, dimension);
          }}
          orientation="portrait"
          playerRef={playerReference}
          videoId={youtubeId}
          videoPlayMode={isPlayable ? VideoPlayMode.Autoplay : VideoPlayMode.Unset}
          width="100%"
        />
      </PlayerContainer>
      <PlayerOverlay onClick={handleClick}>
        {headline && (
          <TitleOverlay>
            <TitleText>{headline}</TitleText>
          </TitleOverlay>
        )}
        <ButtonContainer ref={buttonContainerReference}>
          <MuteButton $unmute={!modalState?.mute} onClick={toggleMuteVideo} />

          <StyledShareButton
            placement="left-end"
            position="scmp_shorts"
            reference={content}
            variant="video-short"
          />

          <RedirectButton reference={content} />
        </ButtonContainer>
      </PlayerOverlay>

      {isActiveSlide && (
        <CloseButton onClick={onClose}>
          <StyledCloseButtonIcon />
        </CloseButton>
      )}
    </Container>
  );
};

VideoShortCarouselPlayer.displayName = "VideoShortCarouselPlayer";
